import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Empty, Progress, Space, Spin, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ApartmentOutlined,
  ArrowRightOutlined,
  BarChartOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  FundOutlined,
  ReloadOutlined,
  RiseOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';
import api from '../services/api';
import { useAppSelector } from '../store';
import './Dashboard.css';

const { Title, Text } = Typography;

interface DashboardSummary {
  usersTotal: number;
  todayNewUsers: number;
  weekNewUsers: number;
  providersTotal: number;
  activeProviders: number;
  certifiedProviders: number;
  pendingCertificationCount: number;
  todayNewProviders: number;
  weekNewProviders: number;
  productsTotal: number;
  onlineProducts: number;
  pendingPayOrders: number;
  todayPaidOrders: number;
  todayPaidAmount: number;
  monthPaidAmount: number;
  todayNewOrders: number;
  weekNewOrders: number;
  orderPoolCount: number;
  todayServiceItems: number;
  pendingRefundReviews: number;
  processingRefunds: number;
  pendingWithdrawReviews: number;
}

interface DashboardStatusItem {
  status: string;
  count: number;
}

interface DashboardPaymentTrendItem {
  date: string;
  orderCount: number;
  paidAmount: number;
}

interface DashboardTopProductItem {
  productId: string;
  productName: string;
  orderCount: number;
  paidAmount: number;
}

interface DashboardRecentOrderItem {
  id: string;
  orderNo: string;
  productName: string;
  contactName: string;
  totalAmount: number;
  payStatus: string;
  status: string;
  createdAt: string;
  paidAt?: string;
}

interface DashboardPendingTaskItem {
  key: string;
  title: string;
  count: number;
  path: string;
  tone: string;
}

interface DashboardStaffSnapshot {
  orderModeEnabled: number;
  autoAcceptEnabled: number;
  totalWithdrawableAmt: number;
}

interface DashboardOverview {
  generatedAt: string;
  summary: DashboardSummary;
  orderStatus: DashboardStatusItem[];
  paymentTrend: DashboardPaymentTrendItem[];
  topProducts: DashboardTopProductItem[];
  recentOrders: DashboardRecentOrderItem[];
  pendingTasks: DashboardPendingTaskItem[];
  staff: DashboardStaffSnapshot;
}

const statusTextMap: Record<string, string> = {
  pending: '待派单',
  assigned: '待接单',
  waiting: '待服务',
  checkin_pending: '待签到',
  serving: '服务中',
  done: '已完成',
  accepted_review: '已评价',
  cancelled: '已取消',
  aftersale: '售后中',
  refunded: '已退款',
};

const statusColorMap: Record<string, string> = {
  pending: 'processing',
  assigned: 'warning',
  waiting: 'blue',
  checkin_pending: 'cyan',
  serving: 'purple',
  done: 'success',
  accepted_review: 'success',
  cancelled: 'default',
  aftersale: 'orange',
  refunded: 'magenta',
};

const payStatusTextMap: Record<string, string> = {
  unpaid: '待支付',
  paid: '已支付',
};

const toneColorMap: Record<string, string> = {
  blue: '#2f6bff',
  red: '#ff5a6b',
  orange: '#ff9a3d',
  purple: '#7a5cff',
  gold: '#d7a11e',
  cyan: '#18a0c9',
};

const money = (value?: number) => `￥${Number(value || 0).toFixed(2)}`;

const formatDateTime = (value?: string) => {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate()
  ).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAppSelector(state => state.auth);
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState<DashboardOverview | null>(null);

  const fetchOverview = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/dashboard/overview');
      setOverview(response.data);
    } catch (error: any) {
      message.error(error?.response?.data?.message || '加载工作台数据失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchOverview();
  }, [fetchOverview]);

  const summary = overview?.summary;

  const metricCards = useMemo(
    () => [
      {
        key: 'todayAmount',
        title: '今日支付金额',
        value: money(summary?.todayPaidAmount),
        note: `今日支付订单 ${summary?.todayPaidOrders || 0} 单`,
        icon: <DollarOutlined />,
        accent: 'blue',
      },
      {
        key: 'monthAmount',
        title: '本月成交金额',
        value: money(summary?.monthPaidAmount),
        note: '按已支付订单累计',
        icon: <FundOutlined />,
        accent: 'purple',
      },
      {
        key: 'orderPool',
        title: '订单池待派单',
        value: `${summary?.orderPoolCount || 0}`,
        note: '待人工处理的订单池记录',
        icon: <ShoppingCartOutlined />,
        accent: 'orange',
      },
      {
        key: 'serviceToday',
        title: '今日待服务',
        value: `${summary?.todayServiceItems || 0}`,
        note: '今日待签到或服务中的子订单',
        icon: <CalendarOutlined />,
        accent: 'cyan',
      },
      {
        key: 'refundReview',
        title: '退款待审核',
        value: `${summary?.pendingRefundReviews || 0}`,
        note: `处理中 ${summary?.processingRefunds || 0} 条`,
        icon: <ClockCircleOutlined />,
        accent: 'red',
      },
      {
        key: 'staffActive',
        title: '当前在岗服务人员',
        value: `${summary?.activeProviders || 0}`,
        note: `认证通过 ${summary?.certifiedProviders || 0} 人`,
        icon: <TeamOutlined />,
        accent: 'green',
      },
    ],
    [summary]
  );

  const paymentTrendOption = useMemo(() => {
    const trend = overview?.paymentTrend || [];
    return {
      color: ['#2f6bff', '#7a5cff'],
      tooltip: { trigger: 'axis' },
      legend: { data: ['支付金额', '支付单量'], top: 0, right: 0 },
      grid: { left: 12, right: 12, top: 50, bottom: 10, containLabel: true },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: trend.map(item => item.date.slice(5)),
        axisLine: { lineStyle: { color: '#d6dfeb' } },
      },
      yAxis: [
        {
          type: 'value',
          name: '金额',
          splitLine: { lineStyle: { color: '#eef3f9' } },
        },
        {
          type: 'value',
          name: '单量',
          splitLine: { show: false },
        },
      ],
      series: [
        {
          name: '支付金额',
          type: 'line',
          smooth: true,
          symbolSize: 8,
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(47,107,255,0.24)' },
                { offset: 1, color: 'rgba(47,107,255,0.02)' },
              ],
            },
          },
          data: trend.map(item => Number(item.paidAmount || 0).toFixed(2)),
        },
        {
          name: '支付单量',
          type: 'bar',
          yAxisIndex: 1,
          barWidth: 18,
          itemStyle: { borderRadius: [8, 8, 0, 0] },
          data: trend.map(item => item.orderCount || 0),
        },
      ],
    };
  }, [overview]);

  const orderStatusOption = useMemo(() => {
    const statusItems = overview?.orderStatus || [];
    return {
      color: ['#2f6bff', '#ff9a3d', '#3cb179', '#7a5cff', '#ff5a6b', '#2dc5c4', '#8b98a9'],
      tooltip: { trigger: 'item' },
      legend: { bottom: 0, itemWidth: 10, itemHeight: 10 },
      series: [
        {
          type: 'pie',
          radius: ['52%', '74%'],
          center: ['50%', '44%'],
          label: {
            formatter: '{b}\n{c}',
            color: '#3b4656',
            fontSize: 12,
          },
          data: statusItems.map(item => ({
            name: statusTextMap[item.status] || item.status,
            value: item.count,
          })),
        },
      ],
    };
  }, [overview]);

  const topProductsOption = useMemo(() => {
    const items = [...(overview?.topProducts || [])].reverse();
    return {
      color: ['#5c8bff'],
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      grid: { left: 10, right: 16, top: 12, bottom: 12, containLabel: true },
      xAxis: {
        type: 'value',
        splitLine: { lineStyle: { color: '#eef3f9' } },
      },
      yAxis: {
        type: 'category',
        data: items.map(item => item.productName || '未命名服务'),
        axisTick: { show: false },
        axisLine: { show: false },
      },
      series: [
        {
          type: 'bar',
          data: items.map(item => item.paidAmount || 0),
          barWidth: 14,
          itemStyle: {
            borderRadius: [0, 10, 10, 0],
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 1,
              y2: 0,
              colorStops: [
                { offset: 0, color: '#6da2ff' },
                { offset: 1, color: '#2f6bff' },
              ],
            },
          },
          label: {
            show: true,
            position: 'right',
            formatter: ({ value }: { value: number }) => money(Number(value || 0)),
            color: '#3b4656',
          },
        },
      ],
    };
  }, [overview]);

  const growthOverviewOption = useMemo(() => {
    return {
      color: ['#2f6bff', '#7a5cff'],
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      legend: { top: 0, right: 0, data: ['今日新增', '本周新增'] },
      grid: { left: 16, right: 16, top: 48, bottom: 8, containLabel: true },
      xAxis: {
        type: 'category',
        data: ['用户', '订单', '服务人员'],
        axisTick: { show: false },
        axisLine: { lineStyle: { color: '#d6dfeb' } },
      },
      yAxis: {
        type: 'value',
        splitLine: { lineStyle: { color: '#eef3f9' } },
      },
      series: [
        {
          name: '今日新增',
          type: 'bar',
          barWidth: 18,
          itemStyle: { borderRadius: [8, 8, 0, 0] },
          data: [summary?.todayNewUsers || 0, summary?.todayNewOrders || 0, summary?.todayNewProviders || 0],
        },
        {
          name: '本周新增',
          type: 'bar',
          barWidth: 18,
          itemStyle: { borderRadius: [8, 8, 0, 0] },
          data: [summary?.weekNewUsers || 0, summary?.weekNewOrders || 0, summary?.weekNewProviders || 0],
        },
      ],
    };
  }, [summary]);

  const growthStats = useMemo(
    () => [
      {
        key: 'users',
        title: '新增用户',
        today: summary?.todayNewUsers || 0,
        week: summary?.weekNewUsers || 0,
      },
      {
        key: 'orders',
        title: '新增订单',
        today: summary?.todayNewOrders || 0,
        week: summary?.weekNewOrders || 0,
      },
      {
        key: 'providers',
        title: '新增服务人员',
        today: summary?.todayNewProviders || 0,
        week: summary?.weekNewProviders || 0,
      },
    ],
    [summary]
  );

  const recentOrderColumns: ColumnsType<DashboardRecentOrderItem> = [
    {
      title: '订单号',
      dataIndex: 'orderNo',
      key: 'orderNo',
      width: 220,
      render: value => <span className="dashboard-order-no">{value}</span>,
    },
    {
      title: '服务项目',
      dataIndex: 'productName',
      key: 'productName',
      ellipsis: true,
      render: value => value || '--',
    },
    {
      title: '联系人',
      dataIndex: 'contactName',
      key: 'contactName',
      width: 100,
      render: value => value || '--',
    },
    {
      title: '金额',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 110,
      render: value => <span className="dashboard-money">{money(value)}</span>,
    },
    {
      title: '支付状态',
      dataIndex: 'payStatus',
      key: 'payStatus',
      width: 100,
      render: value => <Tag color={value === 'paid' ? 'success' : 'default'}>{payStatusTextMap[value] || value || '--'}</Tag>,
    },
    {
      title: '订单状态',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: value => <Tag color={statusColorMap[value] || 'default'}>{statusTextMap[value] || value || '--'}</Tag>,
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
      render: value => formatDateTime(value),
    },
  ];

  const taskCompletionPercent = useMemo(() => {
    const total =
      (summary?.orderPoolCount || 0) +
      (summary?.pendingRefundReviews || 0) +
      (summary?.processingRefunds || 0) +
      (summary?.pendingCertificationCount || 0) +
      (summary?.pendingWithdrawReviews || 0);
    if (!total) return 100;
    const done = Math.max((summary?.todayPaidOrders || 0) + (summary?.todayServiceItems || 0), 0);
    return Math.min(Math.round((done / (done + total)) * 100), 100);
  }, [summary]);

  return (
    <div className="dashboard-shell">
      <div className="dashboard-hero">
        <div className="dashboard-hero__content">
          <div className="dashboard-hero__eyebrow">
            <ApartmentOutlined />
            <span>悠米宝贝</span>
          </div>
          <Title level={2} className="dashboard-hero__title">
            宠物服务运营工作台
          </Title>
          <Text className="dashboard-hero__desc">
            聚合订单、支付、退款、订单池和服务人员核心数据，管理端可以直接在这里看到今天最需要处理的业务。
          </Text>
        </div>
        <div className="dashboard-hero__aside">
          <div className="dashboard-hero__tag">
            <CalendarOutlined />
            <span>数据时间 {formatDateTime(overview?.generatedAt)}</span>
          </div>
          <div className="dashboard-hero__tag">
            <ShopOutlined />
            <span>在线服务 {summary?.onlineProducts || 0} 项</span>
          </div>
          <Button type="primary" icon={<ReloadOutlined />} onClick={() => void fetchOverview()}>
            刷新数据
          </Button>
        </div>
      </div>

      <Spin spinning={loading}>
        {!overview ? (
          <Card className="dashboard-empty-card">
            <Empty description="暂无工作台数据" />
          </Card>
        ) : (
          <>
            <div className="dashboard-metrics">
              {metricCards.map(card => (
                <Card key={card.key} className={`dashboard-metric-card accent-${card.accent}`} variant="borderless">
                  <div className="dashboard-metric-card__icon">{card.icon}</div>
                  <div className="dashboard-metric-card__title">{card.title}</div>
                  <div className="dashboard-metric-card__value">{card.value}</div>
                  <div className="dashboard-metric-card__note">{card.note}</div>
                </Card>
              ))}
            </div>

            <div className="dashboard-grid dashboard-grid-main">
              <Card
                className="dashboard-panel dashboard-panel-wide"
                title={
                  <Space>
                    <FundOutlined />
                    <span>近 7 日支付趋势</span>
                  </Space>
                }
                extra={<Text type="secondary">金额 + 单量双维度</Text>}
              >
                <ReactECharts option={paymentTrendOption} style={{ height: 320 }} />
              </Card>

              <Card
                className="dashboard-panel"
                title={
                  <Space>
                    <ClockCircleOutlined />
                    <span>待办中心</span>
                  </Space>
                }
              >
                <div className="dashboard-task-progress">
                  <div>
                    <div className="dashboard-task-progress__label">今日处理推进度</div>
                    <div className="dashboard-task-progress__value">{taskCompletionPercent}%</div>
                  </div>
                  <Progress
                    percent={taskCompletionPercent}
                    showInfo={false}
                    strokeColor={{ '0%': '#2f6bff', '100%': '#7a5cff' }}
                    railColor="#eef3fb"
                  />
                </div>

                <div className="dashboard-task-list">
                  {overview.pendingTasks.map(task => (
                    <button
                      key={task.key}
                      type="button"
                      className="dashboard-task-item"
                      onClick={() => navigate(task.path)}
                    >
                      <div className="dashboard-task-item__main">
                        <span
                          className="dashboard-task-item__dot"
                          style={{ backgroundColor: toneColorMap[task.tone] || '#2f6bff' }}
                        />
                        <div>
                          <div className="dashboard-task-item__title">{task.title}</div>
                          <div className="dashboard-task-item__sub">点击进入对应管理页面</div>
                        </div>
                      </div>
                      <div className="dashboard-task-item__meta">
                        <span className="dashboard-task-item__count">{task.count}</span>
                        <ArrowRightOutlined />
                      </div>
                    </button>
                  ))}
                </div>
              </Card>
            </div>

            <div className="dashboard-grid dashboard-grid-growth">
              <Card
                className="dashboard-panel"
                title={
                  <Space>
                    <RiseOutlined />
                    <span>新增概览</span>
                  </Space>
                }
                extra={<Text type="secondary">今天 / 本周</Text>}
              >
                <div className="dashboard-growth-list">
                  {growthStats.map(item => (
                    <div key={item.key} className="dashboard-growth-item">
                      <div className="dashboard-growth-item__title">{item.title}</div>
                      <div className="dashboard-growth-item__row">
                        <span className="dashboard-growth-item__label">今天</span>
                        <span className="dashboard-growth-item__value">{item.today}</span>
                      </div>
                      <div className="dashboard-growth-item__row">
                        <span className="dashboard-growth-item__label">本周</span>
                        <span className="dashboard-growth-item__value">{item.week}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              <Card
                className="dashboard-panel"
                title={
                  <Space>
                    <BarChartOutlined />
                    <span>新增对比图</span>
                  </Space>
                }
              >
                <ReactECharts option={growthOverviewOption} style={{ height: 280 }} />
              </Card>
            </div>

            <div className="dashboard-grid dashboard-grid-secondary">
              <Card
                className="dashboard-panel"
                title={
                  <Space>
                    <ShoppingCartOutlined />
                    <span>订单状态分布</span>
                  </Space>
                }
              >
                <ReactECharts option={orderStatusOption} style={{ height: 320 }} />
              </Card>

              <Card
                className="dashboard-panel"
                title={
                  <Space>
                    <CheckCircleOutlined />
                    <span>热销服务排行</span>
                  </Space>
                }
              >
                <ReactECharts option={topProductsOption} style={{ height: 320 }} />
              </Card>

              <Card
                className="dashboard-panel"
                title={
                  <Space>
                    <TeamOutlined />
                    <span>服务人员概览</span>
                  </Space>
                }
              >
                <div className="dashboard-side-block">
                  <div className="dashboard-mini-stat">
                    <span className="dashboard-mini-stat__label">服务人员总数</span>
                    <span className="dashboard-mini-stat__value">{summary?.providersTotal || 0}</span>
                  </div>
                  <div className="dashboard-mini-stat">
                    <span className="dashboard-mini-stat__label">接单模式开启</span>
                    <span className="dashboard-mini-stat__value">{overview.staff.orderModeEnabled || 0}</span>
                  </div>
                  <div className="dashboard-mini-stat">
                    <span className="dashboard-mini-stat__label">自动接单开启</span>
                    <span className="dashboard-mini-stat__value">{overview.staff.autoAcceptEnabled || 0}</span>
                  </div>
                  <div className="dashboard-mini-stat">
                    <span className="dashboard-mini-stat__label">待认证审核</span>
                    <span className="dashboard-mini-stat__value">{summary?.pendingCertificationCount || 0}</span>
                  </div>
                </div>

                <div className="dashboard-balance-card">
                  <div className="dashboard-balance-card__label">服务人员待提现总额</div>
                  <div className="dashboard-balance-card__value">{money(overview.staff.totalWithdrawableAmt)}</div>
                  <Button type="link" onClick={() => navigate('/staff/withdraw-rule')}>
                    查看提现规则
                  </Button>
                </div>
              </Card>
            </div>

            <Card
              className="dashboard-panel dashboard-table-panel"
              title={
                <Space>
                  <CalendarOutlined />
                  <span>最新订单</span>
                </Space>
              }
              extra={
                <Button type="link" onClick={() => navigate('/payment/order')}>
                  查看支付订单
                </Button>
              }
            >
              <Table<DashboardRecentOrderItem>
                rowKey="id"
                columns={recentOrderColumns}
                dataSource={overview.recentOrders}
                pagination={false}
                scroll={{ x: 980 }}
              />
            </Card>
          </>
        )}
      </Spin>
    </div>
  );
};

export default Dashboard;
